// src/api/outlookMailApi.ts

import { constructRequest } from "./utils/apiHandler";
import { handleAuthRejection } from "./utils/errorHandler";
import { createClientErrorInfo } from "./utils/clientErrorHandler";
import { createLogger } from "../utils/logger";

const log = createLogger("OutlookMailApi");

const API_BASE_URL = process.env.API_BASE_URL;

/**
 * Custom error class for Outlook mail API errors
 */
export class OutlookMailApiError extends Error {
  errorCode: string;
  statusCode: number;

  constructor(message: string, errorCode: string, statusCode: number) {
    super(message);
    this.name = "OutlookMailApiError";
    this.errorCode = errorCode;
    this.statusCode = statusCode;
  }
}

// --- Request / response types (matching BE schemas) ---

export interface MailSendRequest {
  action: "send" | "draft";
  subject: string;
  body_html: string;
  to: string[];
  cc?: string[];
  attachment_base64?: string;
  attachment_name?: string;
}

export interface MailSendResponse {
  action: string;
  subject: string;
  to_recipients: string[];
  cc_recipients: string[];
  has_attachment: boolean;
  attachment_name?: string;
}

export interface PersonResult {
  display_name: string;
  email_address: string;
  job_title?: string;
  department?: string;
}

/**
 * Send or draft an email via the backend Outlook mail endpoint.
 *
 * @param request - Mail send/draft payload
 * @param accessToken - Bearer token for Graph API auth
 * @returns MailSendResponse on success
 */
export async function sendOutlookMail(
  request: MailSendRequest,
  accessToken: string
): Promise<MailSendResponse> {
  const url = `${API_BASE_URL}/mail/send`;

  log.info("Sending outlook mail request", {
    url,
    action: request.action,
    subject: request.subject,
    toCount: request.to.length,
    ccCount: request.cc?.length ?? 0,
    hasAttachment: !!request.attachment_base64,
  });

  try {
    const body = JSON.stringify(request);

    // Use constructRequest for auth headers, then override for JSON body
    const apiRequest = constructRequest(url, "POST", accessToken, undefined);

    const headers = new Headers(apiRequest.headers);
    headers.set("Content-Type", "application/json");

    const response = await fetch(apiRequest.url, {
      method: "POST",
      headers,
      body,
    });

    // Emit the auth-rejection telemetry and, on 401, trigger the token refresh
    // bridge. Called BEFORE the body is consumed so a non-JSON 401 body (e.g.
    // a WAF block page) still triggers the refresh.
    handleAuthRejection(response);

    const data = await response.json();

    if (!response.ok) {
      log.error("Outlook mail API returned error", {
        status: response.status,
        data,
      });

      const message =
        typeof data.detail === "string"
          ? data.detail
          : data.detail?.message || data.message || data.error || "Failed to send mail";

      const errorCode =
        (typeof data.detail === "object" && data.detail?.error_code) ||
        data.error_code ||
        "MAIL_SEND_ERROR";

      throw new OutlookMailApiError(message, errorCode, response.status);
    }

    log.info("Outlook mail request succeeded", {
      action: data.action,
      subject: data.subject,
      toRecipients: data.to_recipients,
    });

    return data as MailSendResponse;
  } catch (error) {
    if (error instanceof OutlookMailApiError) {
      throw error;
    }

    log.error("Failed to send outlook mail", {
      error: error instanceof Error ? error.message : String(error),
    });

    const clientError = createClientErrorInfo(error, "general");
    throw new OutlookMailApiError(clientError.message, clientError.errorCode, 0);
  }
}

/**
 * Search people via Microsoft Graph (proxied through the backend).
 *
 * @param query - Search string (name or email)
 * @param accessToken - Bearer token for Graph API auth
 * @param top - Maximum results to return (default 10)
 * @returns Array of PersonResult
 */
export async function searchPeople(
  query: string,
  accessToken: string,
  top: number = 10
): Promise<PersonResult[]> {
  const url = `${API_BASE_URL}/mail/people?q=${encodeURIComponent(query)}&top=${top}`;

  log.info("Searching people", { url, query, top });

  try {
    const apiRequest = constructRequest(url, "GET", accessToken);

    const response = await fetch(apiRequest);

    handleAuthRejection(response);

    const data = await response.json();

    if (!response.ok) {
      log.error("People search API returned error", {
        status: response.status,
        data,
      });

      const message =
        typeof data.detail === "string"
          ? data.detail
          : data.detail?.message || data.message || data.error || "Failed to search people";

      const errorCode =
        (typeof data.detail === "object" && data.detail?.error_code) ||
        data.error_code ||
        "PEOPLE_SEARCH_ERROR";

      throw new OutlookMailApiError(message, errorCode, response.status);
    }

    const results: PersonResult[] = data.results ?? [];

    log.info("People search succeeded", { resultCount: results.length });

    return results;
  } catch (error) {
    if (error instanceof OutlookMailApiError) {
      throw error;
    }

    log.error("Failed to search people", {
      error: error instanceof Error ? error.message : String(error),
    });

    const clientError = createClientErrorInfo(error, "general");
    throw new OutlookMailApiError(clientError.message, clientError.errorCode, 0);
  }
}
